/**
 * CustomerHistoryModal — full-screen home for the rich history timeline.
 *
 * Wraps <CustomerHistoryTimeline variant="fullscreen"> in a dedicated modal so
 * the office gets a wide, distraction-free read of everything that has ever
 * happened to a customer (or one of their services). Launched from the
 * CustomerDetailModal Activity tab.
 *
 * z-[440]: above CustomerDetailModal (z-[200] page / z-[400] provider). The
 * timeline's "Open in Jobs / Faults / deal workspace" deep-links navigate via
 * router (closing this modal first), so no nested-modal layering is needed.
 */

import { useMemo, useEffect } from 'react';
import { X, History, Wrench, Zap, Briefcase, Wifi, Phone, DollarSign } from 'lucide-react';
import { Job, CustomerService, CallLog } from '@/types/schema';
import type { Deal } from '@/types/theo-workflow.types';
import type { Fault } from '@/types/fault.types';
import type { CustomerActivityLogEntry } from '@/services/aggregate-customer.service';
import { CustomerHistoryTimeline } from './CustomerHistoryTimeline';
import { formatCurrency } from '@/utils/pricing';

interface CustomerHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerName?: string;
  subtitle?: string;
  jobs: Job[];
  faults: Fault[];
  deals: Deal[];
  services: CustomerService[];
  serviceRequests?: import('@/types/service-request.types').ServiceRequest[];
  calls: CallLog[];
  notes?: import('@/types/schema').Note[];
  auditEntries?: CustomerActivityLogEntry[];
}

export function CustomerHistoryModal({
  isOpen, onClose, customerName, subtitle,
  jobs, faults, deals, services, serviceRequests, calls, notes, auditEntries,
}: CustomerHistoryModalProps) {
  // Esc closes ONLY this modal — capture phase + stopImmediatePropagation so
  // the parent CustomerDetailModal's document-level Esc listener doesn't also
  // fire and close the customer record underneath us.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, onClose]);

  const mrr = useMemo(
    () => services
      .filter((s) => s.status === 'Active' && s.billingType !== 'one-time' && s.monthlyPrice)
      .reduce((sum, s) => sum + (s.monthlyPrice || 0), 0),
    [services],
  );

  const stats = useMemo(() => ([
    { icon: Wrench, label: 'Jobs', value: jobs.length, color: 'text-blue-400' },
    { icon: Zap, label: 'Faults', value: faults.length, color: 'text-rose-400' },
    { icon: Briefcase, label: 'Deals', value: deals.length, color: 'text-emerald-400' },
    { icon: Wifi, label: 'Services', value: services.length, color: 'text-purple-400' },
    { icon: Phone, label: 'Calls', value: calls.length, color: 'text-sky-400' },
  ]), [jobs.length, faults.length, deals.length, services.length, calls.length]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[440] bg-black/70 backdrop-blur-sm flex items-stretch justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${customerName || 'Customer'} history`}
    >
      <div
        className="bg-surface-card w-full sm:max-w-5xl sm:rounded-2xl border-theme sm:border shadow-2xl flex flex-col overflow-hidden sm:my-2"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-gradient-to-r from-brand-secondary/15 to-transparent border-b border-theme px-5 sm:px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-brand-secondary/20 flex items-center justify-center shrink-0">
                <History className="w-5 h-5 text-brand-secondary" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-theme-primary truncate">{customerName || 'Customer'} — History</h2>
                <p className="text-xs text-theme-muted">{subtitle || 'Complete timeline · jobs, faults, deals, services & calls'}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-surface-hover rounded-lg transition-colors shrink-0" title="Close (Esc)">
              <X className="w-5 h-5 text-theme-muted hover:text-theme-primary" />
            </button>
          </div>

          {/* Stat strip */}
          <div className="flex items-center gap-2 sm:gap-3 mt-3 flex-wrap">
            {stats.map((s) => (
              <div key={s.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-hover border border-theme-soft">
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                <span className="text-sm font-bold text-theme-primary tabular-nums">{s.value}</span>
                <span className="text-[11px] text-theme-muted">{s.label}</span>
              </div>
            ))}
            {mrr > 0 && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
                <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-sm font-bold text-emerald-300 tabular-nums">
                  {formatCurrency(mrr, { maximumFractionDigits: 0 })}
                </span>
                <span className="text-[11px] text-emerald-400/70">MRR</span>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable timeline — one elevation below the cards so they pop. */}
        <div className="flex-1 overflow-y-auto bg-surface px-4 sm:px-6 py-5">
          <CustomerHistoryTimeline
            variant="fullscreen"
            jobs={jobs}
            faults={faults}
            deals={deals}
            services={services}
            serviceRequests={serviceRequests}
            calls={calls}
            notes={notes}
            auditEntries={auditEntries}
            onRequestHostClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}
